import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Client } from '../types'
import { 
  MagnifyingGlassIcon,
  UserIcon,
  ChevronDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface FuzzyClientSearchProps {
  clients: Client[]
  selectedClient: Client | null
  onClientSelect: (client: Client | null) => void
  placeholder?: string
  disabled?: boolean
}

const FuzzyClientSearch = ({ 
  clients, 
  selectedClient, 
  onClientSelect, 
  placeholder = "Search for a client...",
  disabled = false
}: FuzzyClientSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [filteredClients, setFilteredClients] = useState<Client[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fuzzy search function
  const fuzzySearch = (searchTerm: string, clients: Client[]): Client[] => {
    if (!searchTerm.trim()) return clients

    const searchLower = searchTerm.toLowerCase()
    
    return clients
      .map(client => {
        let score = 0
        const name = client.name.toLowerCase()
        const company = client.company?.toLowerCase() || ''
        const email = client.email.toLowerCase()
        
        // Exact matches get highest score
        if (name.includes(searchLower)) score += 100
        if (company.includes(searchLower)) score += 80
        if (email.includes(searchLower)) score += 60
        
        // Character-by-character fuzzy matching
        let nameIndex = 0
        let searchIndex = 0
        while (nameIndex < name.length && searchIndex < searchLower.length) {
          if (name[nameIndex] === searchLower[searchIndex]) {
            score += 10
            searchIndex++
          }
          nameIndex++
        }
        
        // Bonus for matching all characters
        if (searchIndex === searchLower.length) score += 50
        
        return { client, score }
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(item => item.client)
      .slice(0, 10) // Limit to top 10 results
  }

  useEffect(() => {
    const filtered = fuzzySearch(searchTerm, clients)
    setFilteredClients(filtered)
    setHighlightedIndex(-1)
  }, [searchTerm, clients])

  useEffect(() => {
    if (selectedClient) {
      setSearchTerm(selectedClient.name)
      setIsOpen(false)
    }
  }, [selectedClient])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev < filteredClients.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredClients.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredClients[highlightedIndex]) {
          handleClientSelect(filteredClients[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleClientSelect = (client: Client) => {
    onClientSelect(client)
    setSearchTerm(client.name)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleClearSelection = () => {
    onClientSelect(null)
    setSearchTerm('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setIsOpen(true)
    
    // Clear selection if user is typing and it doesn't match selected client
    if (selectedClient && value !== selectedClient.name) {
      onClientSelect(null)
    }
  }

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true)
    }
  }

  const handleInputBlur = () => {
    // Delay closing to allow for click events on dropdown items
    setTimeout(() => {
      setIsOpen(false)
      setHighlightedIndex(-1)
    }, 150)
  }

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        />
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {selectedClient && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="p-1 text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-gray-800/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-xl max-h-60 overflow-y-auto"
          >
            {filteredClients.length > 0 ? (
              <div className="py-2">
                {filteredClients.map((client, index) => (
                  <motion.button
                    key={client.id}
                    type="button"
                    onClick={() => handleClientSelect(client)}
                    className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors ${
                      index === highlightedIndex ? 'bg-white/10' : ''
                    }`}
                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{client.name}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          {client.company && (
                            <>
                              <span className="truncate">{client.company}</span>
                              <span>•</span>
                            </>
                          )}
                          <span className="truncate">{client.email}</span>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <UserIcon className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">
                  {searchTerm ? 'No clients found' : 'No clients available'}
                </p>
                {searchTerm && (
                  <p className="text-gray-500 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FuzzyClientSearch 